import { useState, useEffect } from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormHelperText from '@mui/material/FormHelperText';
import { MSG_MAX_LENGTH } from '../common/errorCodes';

const TextInput = ({
    id,
    name,
    label,
    value,
    onChange,
    onBlur,
    onKeyDown,
    type = 'text',
    placeholder,
    error = false,
    helperText,
    maxLength,
    required = false,
    disabled = false,
    autoFocus = false,
    autoComplete = 'on',
    inputRef,
    inputProps,
    dataAutomationId,
    numericOnly = false,
    multiline = false,
    rows,
    fullWidth = true,
    size = 'small',
    sx,
}) => {
    const [lengthError, setLengthError] = useState(false);

    // When the same TextInput instance is reused across different fields (e.g. stepper
    // steps), maxLength can appear/disappear while lengthError state is retained.
    useEffect(() => {
        setLengthError(false);
    }, [maxLength, id]);

    const handleChange = (event) => {
        if (numericOnly && event.target.value !== '' && !/^\d*$/.test(event.target.value)) {
            return;
        }
        if (maxLength && event.target.value.length > maxLength) {
            setLengthError(true);
            return;
        }
        setLengthError(false);
        if (onChange) {
            onChange(event);
        }
    };

    const hasError = error || lengthError;
    const displayHelperText = (lengthError && maxLength) ? MSG_MAX_LENGTH(maxLength) : helperText;
    const helperId = id ? `${id}-helper` : undefined;

    return (
        <FormControl fullWidth={fullWidth} error={hasError} disabled={disabled} sx={sx}>
            {label && (
                <InputLabel shrink htmlFor={id} required={required}
                    sx={{
                        position: 'relative',
                        transform: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: 'text.primary',
                        '&.Mui-focused': {
                            color: 'text.primary',
                        },
                        '&.Mui-disabled': {
                            color: 'text.primary',
                        },
                    }}
                >
                    {label}
                </InputLabel>
            )}
            <OutlinedInput
                id={id}
                name={name}
                value={value ?? ''}
                onChange={handleChange}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                type={type}
                placeholder={placeholder}
                autoFocus={autoFocus}
                inputRef={inputRef}
                aria-invalid={hasError || undefined}
                aria-describedby={(displayHelperText && helperId) || undefined}
                inputProps={{
                    autoComplete,
                    ...((dataAutomationId || id) ? { 'data-automation-id': dataAutomationId || id } : {}),
                    ...(numericOnly ? { inputMode: 'numeric', pattern: '[0-9]*' } : {}),
                    ...(required ? { 'aria-required': true } : {}),
                    ...inputProps,
                }}
                multiline={multiline}
                rows={rows}
                size={size}
                notched={false}
            />
            <FormHelperText id={helperId} sx={{ my: 0}}>{displayHelperText || ' '}</FormHelperText>
        </FormControl>
    );
};

export default TextInput;
